import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type CanvasFilterOption = string | { label: string; value: string };

@Component({
  selector: 'lib-canvas-filter-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './canvas-filter-multiselect.component.html',
  styleUrl: './canvas-filter-multiselect.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasFilterMultiselectComponent implements OnInit, OnDestroy, OnChanges {
  /** Stable id for coordinating “only one open” within the datasets modal. */
  @Input({ required: true }) panelKey!: string;
  /** Which panel is allowed open in the group; siblings close when this differs. */
  @Input() exclusiveOpenKey: string | null = null;

  @Input() placeholder = 'Select…';
  @Input() disabled = false;
  @Input() options: CanvasFilterOption[] = [];
  @Input() enableSearchThreshold = 8;
  @Input() emphasis = false;
  /** Selected option values (same pattern as catalogue multiselect). */
  @Input() selectedItems: string[] = [];

  @Output() selectedItemsChange = new EventEmitter<string[]>();
  @Output() exclusivePanelChange = new EventEmitter<{ key: string; open: boolean }>();

  isOpen = false;
  searchTerm = '';

  /** Mirror of `selectedItems` for stable checks during emit cycles */
  private selection: string[] = [];

  private _docClickHandler = (ev: MouseEvent): void => {
    this.onDocumentClick(ev);
  };

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.selection = [...(this.selectedItems ?? [])];
    document.addEventListener('click', this._docClickHandler, { capture: true });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClickHandler, { capture: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedItems']) {
      this.selection = [...(this.selectedItems ?? [])];
    }
    if (
      changes['exclusiveOpenKey'] &&
      this.exclusiveOpenKey !== this.panelKey &&
      this.isOpen
    ) {
      this.isOpen = false;
    }
  }

  onDocumentClick(ev: MouseEvent): void {
    const root = this.host.nativeElement;
    const t = ev.target as Node | null;
    if (t && !root.contains(t) && this.isOpen) {
      this.isOpen = false;
      this.exclusivePanelChange.emit({ key: this.panelKey, open: false });
    }
  }

  get normalizedOptions(): { label: string; value: string }[] {
    return (this.options ?? []).map((o) =>
      typeof o === 'string' ? { label: o, value: o } : { ...o },
    );
  }

  get showSearch(): boolean {
    return this.normalizedOptions.length >= this.enableSearchThreshold;
  }

  get filteredOptions(): { label: string; value: string }[] {
    const q = this.searchTerm.trim().toLowerCase();
    const all = this.normalizedOptions;
    if (!q) return all;
    return all.filter((o) => o.label.toLowerCase().includes(q));
  }

  togglePanel(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.disabled || this.normalizedOptions.length === 0) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
    }
    this.exclusivePanelChange.emit({ key: this.panelKey, open: this.isOpen });
  }

  isSelected(value: string): boolean {
    return this.selection.includes(value);
  }

  toggleValue(value: string, ev?: MouseEvent): void {
    ev?.stopPropagation();
    const next = [...this.selection];
    const i = next.indexOf(value);
    if (i >= 0) next.splice(i, 1);
    else next.push(value);
    this.selection = next;
    this.selectedItemsChange.emit(next);
  }

  clear(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.disabled || this.selection.length === 0) return;
    this.selection = [];
    this.selectedItemsChange.emit([]);
  }

  labelForValue(value: string): string {
    const o = this.normalizedOptions.find((x) => x.value === value);
    return o?.label ?? value;
  }

  get chipValues(): string[] {
    return this.selection;
  }

  get selectedCount(): number {
    return this.selection.length;
  }
}
